import { MacWindow } from './MacWindow';
import React, { useState, useEffect } from 'react';
import { 
  SubstratePreset, 
  getAvailableSubstrates,
  getAvailableSubstratesAsync, 
  saveCustomSubstrate, 
  deleteCustomSubstrate
} from '../../utils/substratePresets';
import { 
  Layers, 
  Plus, 
  Trash2, 
  X, 
  Check, 
  RotateCcw, 
  Sparkles
} from 'lucide-react';
import { useToast } from '../../store/ToastContext';
import { useRealtimeCurrency } from '../../hooks/useRealtimeCurrency';

interface SubstrateCustomizerModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentSubstrateId?: string;
  currentCostPerSheet?: number;
  currencySymbol?: string;
  onSelectSubstrate: (preset: SubstratePreset, customCost?: number) => void;
}

export const SubstrateCustomizerModal: React.FC<SubstrateCustomizerModalProps> = ({
  isOpen,
  onClose,
  currentSubstrateId = 'standard_vinyl',
  currentCostPerSheet,
  currencySymbol,
  onSelectSubstrate
}) => {
  const { toast } = useToast();
  const { activeSymbol } = useRealtimeCurrency();
  const effectiveCurrencySymbol = currencySymbol || activeSymbol || '₹';
  const [substrates, setSubstrates] = useState<SubstratePreset[]>(() => getAvailableSubstrates());
  const [selectedId, setSelectedId] = useState<string>(currentSubstrateId);
  const [manualCost, setManualCost] = useState<number>(() => {
    if (currentCostPerSheet !== undefined) return currentCostPerSheet;
    const found = getAvailableSubstrates().find(s => s.id === currentSubstrateId);
    return found ? found.defaultCost : 12;
  });

  // Load custom substrates from cloud/local when opened
  useEffect(() => {
    if (isOpen) {
      let isMounted = true;
      getAvailableSubstratesAsync().then((list) => {
        if (isMounted) {
          setSubstrates(list);
        }
      }).catch((err) => {
        console.error('Failed to refresh substrates on open', err);
      });
      return () => { isMounted = false; };
    }
  }, [isOpen]);

  // New substrate form state
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState<SubstratePreset['category']>('Stickers');
  const [newCost, setNewCost] = useState<number>(15);
  const [newGsm, setNewGsm] = useState<number>(150);
  const [newDesc, setNewDesc] = useState('');

  if (!isOpen) return null;

  const handleCreateNew = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) {
      toast.error('Name required', 'Please provide a name for your custom substrate.');
      return;
    }

    try {
      const saved = await saveCustomSubstrate({
        name: newName.trim(),
        category: newCategory,
        defaultCost: Math.max(0, newCost),
        weightGsm: Math.max(10, newGsm),
        description: newDesc.trim() || `${newCategory} substrate with ${newGsm} GSM weight.`
      });

      const updated = await getAvailableSubstratesAsync();
      setSubstrates(updated);
      setSelectedId(saved.id);
      setManualCost(saved.defaultCost);
      setIsAddingNew(false);

      // Reset form
      setNewName('');
      setNewCost(15);
      setNewGsm(150);
      setNewDesc('');

      toast.success('Custom Substrate Added', `"${saved.name}" saved to your substrate library.`);
    } catch (err: any) {
      console.error('Failed to create custom substrate:', err);
      toast.error('Error', err?.message || 'Failed to save custom substrate.');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    try {
      await deleteCustomSubstrate(id);
      const updated = await getAvailableSubstratesAsync();
      setSubstrates(updated);
      if (selectedId === id) {
        setSelectedId('standard_vinyl');
        const std = updated.find(s => s.id === 'standard_vinyl');
        if (std) setManualCost(std.defaultCost);
      }
      toast.info('Substrate Deleted', `Removed "${name}" from your custom materials.`);
    } catch (err: any) {
      console.error('Failed to delete substrate:', err);
      toast.error('Error', err?.message || 'Failed to delete substrate.');
    }
  };

  const handleApply = () => {
    const selectedPreset = substrates.find(s => s.id === selectedId) || substrates[0];
    onSelectSubstrate(selectedPreset, manualCost);
    onClose();
    toast.success(
      'Substrate & Price Applied',
      `Active: ${selectedPreset.name} @ ${effectiveCurrencySymbol}${manualCost.toFixed(2)}/sheet`
    );
  };

  const selectedPreset = substrates.find(s => s.id === selectedId) || substrates[0];

  return (
    <MacWindow isOpen={isOpen} onClose={onClose} title="Substrate Materials & Custom Pricing" maxWidth="max-w-2xl">
      <div className="flex flex-col w-full h-full overflow-hidden">
        
        {/* Header */}
        <div className="p-5 border-b border-zinc-800 bg-zinc-950/80 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-blue-950 text-blue-400 border border-blue-800 flex items-center justify-center">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-zinc-200">Substrate Materials &amp; Custom Pricing</h3>
              <p className="text-xs text-zinc-400">Customize sheet costs, paper weights (GSM), and save shop substrates</p>
            </div>
          </div>
          
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto flex flex-col gap-6">
          
          {/* Active Substrate Quick Manual Cost Adjuster */}
          <div className="p-4 bg-gradient-to-r from-blue-950/50 to-indigo-950/50 rounded-md border border-blue-900/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-blue-400 uppercase tracking-wider">Active Material:</span>
                <span className="text-sm font-extrabold text-zinc-200">{selectedPreset.name}</span>
              </div>
              <p className="text-xs text-zinc-400 mt-0.5">
                Category: <strong className="text-zinc-300">{selectedPreset.category}</strong> • Weight: <strong className="text-zinc-300">{selectedPreset.weightGsm} GSM</strong>
              </p>
            </div>

            <div className="flex items-center gap-2 bg-zinc-900 px-3 py-1.5 rounded-md border border-blue-800 shadow-2xs">
              <label className="text-xs font-bold text-zinc-400 whitespace-nowrap">
                Sheet Price:
              </label>
              <span className="text-sm font-bold text-blue-400 font-mono">{effectiveCurrencySymbol}</span>
              <input
                type="number"
                min="0"
                step="0.5"
                value={manualCost}
                onChange={(e) => setManualCost(parseFloat(e.target.value) || 0)}
                className="w-20 px-2 py-1 bg-zinc-950 border border-zinc-700 rounded-md text-sm font-bold font-mono text-zinc-100 outline-none focus:ring-2 focus:ring-blue-600"
              />
              <span className="text-xs text-zinc-400">/ sheet</span>
            </div>
          </div>

          {/* Substrate Presets Grid */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold text-zinc-300 uppercase tracking-wider flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-blue-400" />
                Select or Customize Substrate Material ({substrates.length})
              </h4>

              <button
                onClick={() => setIsAddingNew(!isAddingNew)}
                className="px-2.5 py-1 bg-blue-950/60 hover:bg-blue-900/80 text-blue-300 border border-blue-800 rounded-md text-xs font-bold flex items-center gap-1 transition-colors cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                {isAddingNew ? 'Cancel New' : 'Add Custom Material'}
              </button>
            </div>

            {/* Add New Custom Substrate Form */}
            {isAddingNew && (
              <form onSubmit={handleCreateNew} className="mb-4 p-4 bg-zinc-950 border border-blue-800 rounded-md space-y-3 animate-in fade-in duration-150">
                <div className="text-xs font-bold text-blue-400 flex items-center gap-1">
                  <Sparkles className="w-3.5 h-3.5" />
                  Define New Shop Material
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold text-zinc-400 block mb-1">Substrate Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. 3M Matte Vinyl 180g"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs font-bold outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] font-bold text-zinc-400 block mb-1">Category</label>
                    <select
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value as any)}
                      className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs font-bold outline-none focus:ring-2 focus:ring-blue-600"
                    >
                      <option value="Stickers">Stickers / Adhesive</option>
                      <option value="Film">Waterproof Film / BOPP</option>
                      <option value="Paper">Paper / Label Stock</option>
                      <option value="Cardstock">Heavy Cardstock / Board</option>
                      <option value="Synthetic">Synthetic (Yupo/PP)</option>
                      <option value="Specialty">Specialty (Foil/Holographic)</option>
                      <option value="Custom">Custom Material</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[11px] font-bold text-zinc-400 block mb-1">
                      Default Sheet Cost ({effectiveCurrencySymbol}) *
                    </label>
                    <div className="flex items-center gap-1 bg-zinc-900 px-2.5 py-1 border border-zinc-700 rounded-md">
                      <span className="text-xs font-bold text-zinc-400 font-mono">{effectiveCurrencySymbol}</span>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        required
                        value={newCost}
                        onChange={(e) => setNewCost(parseFloat(e.target.value) || 0)}
                        className="w-full text-xs font-bold font-mono outline-none text-zinc-100 bg-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] font-bold text-zinc-400 block mb-1">Density / Weight (GSM) *</label>
                    <div className="flex items-center gap-1 bg-zinc-900 px-2.5 py-1 border border-zinc-700 rounded-md">
                      <input
                        type="number"
                        min="10"
                        step="5"
                        required
                        value={newGsm}
                        onChange={(e) => setNewGsm(parseInt(e.target.value, 10) || 100)}
                        className="w-full text-xs font-bold font-mono outline-none text-zinc-100 bg-transparent"
                      />
                      <span className="text-xs text-zinc-400">GSM</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-zinc-400 block mb-1">Notes / Description (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. Matte finish outdoor waterproof vinyl with permanent acrylic glue"
                    value={newDesc}
                    onChange={(e) => setNewDesc(e.target.value)}
                    className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs text-zinc-100 outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setIsAddingNew(false)}
                    className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-md text-xs font-bold transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-1.5 bg-[#1473E6] hover:bg-blue-700 text-white rounded-md text-xs font-bold flex items-center gap-1 transition-colors cursor-pointer shadow-2xs"
                  >
                    <Check className="w-3.5 h-3.5" />
                    Save Material to Library
                  </button>
                </div>
              </form>
            )}

            {/* Presets List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[260px] overflow-y-auto pr-1">
              {substrates.map((preset) => {
                const isSelected = selectedId === preset.id;
                return (
                  <div
                    key={preset.id}
                    onClick={() => {
                      setSelectedId(preset.id);
                      if (preset.id !== 'custom') {
                        setManualCost(preset.defaultCost);
                      }
                    }}
                    className={`p-3 rounded-md border transition-all cursor-pointer flex flex-col justify-between relative group ${
                      isSelected
                        ? 'bg-blue-950/60 border-blue-600 ring-1 ring-blue-500'
                        : 'bg-zinc-950/70 border-zinc-800 hover:bg-zinc-800 hover:border-zinc-700'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-bold text-xs text-zinc-200">{preset.name}</span>
                          {preset.isCustom && (
                            <span className="px-1.5 py-0.2 bg-purple-950/60 text-purple-300 border border-purple-800/60 text-[10px] font-bold rounded">
                              Custom
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-zinc-400 line-clamp-1 mt-0.5">{preset.description}</p>
                      </div>

                      <div className="text-right shrink-0">
                        <span className="text-xs font-extrabold text-zinc-200 font-mono">
                          {effectiveCurrencySymbol}{preset.defaultCost}
                        </span>
                        <span className="block text-[10px] text-zinc-400 font-mono">{preset.weightGsm} GSM</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-800/60 text-[11px]">
                      <span className="text-zinc-400">{preset.category}</span>
                      
                      <div className="flex items-center gap-2">
                        {preset.isCustom && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(preset.id, preset.name);
                            }}
                            className="text-red-500 hover:text-red-400 p-0.5 rounded hover:bg-red-950/30 opacity-80 group-hover:opacity-100 transition-opacity cursor-pointer"
                            title="Delete custom substrate"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <span className={`font-bold ${isSelected ? 'text-blue-400' : 'text-zinc-400'}`}>
                          {isSelected ? '✓ Selected' : 'Select'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 bg-zinc-950 flex items-center justify-between">
          <button
            onClick={() => {
              setManualCost(12);
              setSelectedId('standard_vinyl');
            }}
            className="text-xs text-zinc-500 hover:text-zinc-200 font-semibold flex items-center gap-1 cursor-pointer"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset to Standard (Vinyl ₹12)
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-md text-xs font-bold transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-5 py-2 bg-[#1473E6] hover:bg-blue-700 text-white rounded-md text-xs font-bold transition-colors cursor-pointer shadow-sm flex items-center gap-1.5"
            >
              <Check className="w-4 h-4" />
              Apply Substrate &amp; Price
            </button>
          </div>
        </div>

      </div>
    </MacWindow>
  );
};

